/*
 * WOW Gift - free gifts unlocked by cart value (replaces Rebuy widget 301402).
 *
 * Group A (Rebuy): this file does nothing.
 * Group B (ours):  runs only while WowGiftConfig.discountLive is true.
 *
 * THE RULE THIS FILE EXISTS TO KEEP: a gift is never charged for, and never
 * given when it has not been earned. This file can put a gift in the cart but
 * it cannot make it free - a Shopify automatic discount does that (see
 * storefront/wow-gift/README.md). So after every change the cart is read back
 * and any gift line that costs money is taken out again, for the whole visit.
 *
 * Settings: snippets/wow-gift.liquid (window.WowGiftConfig).
 * Source of truth: storefront/wow-gift/ in the WOW repo - never edit this in
 * the Shopify theme editor, the next deploy overwrites it.
 */
(function (root) {
  'use strict';

  /* ---------- pure helpers (unit-tested in storefront/wow-gift/test) ---------- */

  function isGiftLine(item) {
    return !!(item && item.properties && item.properties._wow_gift != null);
  }

  // Only tiers that can really be given: a threshold and a variant to add.
  function activeTiers(tiers) {
    return (tiers || []).filter(function (t) {
      return t && Number(t.threshold_cents) > 0 && Number(t.variant_id) > 0;
    }).slice().sort(function (a, b) { return a.threshold_cents - b.threshold_cents; });
  }

  // What the shopper is spending on merchandise: what each line costs after
  // its own discounts (so a subscription counts at its subscription price),
  // leaving out our gift lines and the shipping-protection line.
  function giftSubtotal(cart, excludeHandles) {
    var skip = excludeHandles || [];
    return ((cart && cart.items) || []).reduce(function (sum, item) {
      if (isGiftLine(item) || skip.indexOf(item.handle) !== -1) return sum;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
  }

  function emptyState(token) {
    return { token: token || null, added: {}, declined: {}, blocked: {} };
  }

  function copyState(state, token) {
    var s = emptyState(token);
    ['added', 'declined', 'blocked'].forEach(function (k) {
      Object.keys((state && state[k]) || {}).forEach(function (t) { if (state[k][t]) s[k][t] = true; });
    });
    return s;
  }

  /*
   * The whole decision, with no side effects: given the cart, the settings and
   * what this visit remembers, say what to do and what to remember next.
   *
   *   state.added[tier]    we put this gift in the cart and saw it there, free
   *   state.declined[tier] the shopper took it out by hand - never add it back
   *   state.blocked[tier]  it came out costing money - never offer it again
   *
   * Actions: { type: 'add', tier, variant_id }
   *          { type: 'remove', tier, key, reason }   reason: below | not_free |
   *                                                   blocked | duplicate | unknown_tier
   *          { type: 'set_qty', tier, key, quantity: 1 }
   */
  function decide(cart, config, state) {
    var token = (cart && cart.token) || null;
    // a different cart (after an order, say) starts with a clean memory
    var fresh = state && state.token && token && state.token !== token;
    var next = fresh ? emptyState(token) : copyState(state, token || (state && state.token));
    var actions = [];
    if (!config || config.discountLive !== true) return { actions: actions, state: next };

    var tiers = activeTiers(config.tiers);
    var subtotal = giftSubtotal(cart, config.excludeHandles);
    var lines = ((cart && cart.items) || []).filter(isGiftLine);
    var known = {};
    tiers.forEach(function (t) { known[String(t.threshold_cents)] = true; });

    // a gift line from a tier that no longer exists must not stay in the cart
    lines.forEach(function (l) {
      var id = String(l.properties._wow_gift);
      if (!known[id]) actions.push({ type: 'remove', tier: id, key: l.key, reason: 'unknown_tier' });
    });

    // Swap (Kale, 2026-09-30): the shopper gets ONE gift, the best one reached.
    // Reaching the serum takes the candle back out. A gift that came out
    // costing money does not count as the best one, so the step below it stays.
    var best = null;
    if (config.swap === true) {
      tiers.forEach(function (t) {
        var id = String(t.threshold_cents);
        if (subtotal >= t.threshold_cents && !next.blocked[id]) best = id;
      });
    }

    tiers.forEach(function (t) {
      var id = String(t.threshold_cents);
      var mine = lines.filter(function (l) { return String(l.properties._wow_gift) === id; });
      var earned = subtotal >= t.threshold_cents && (config.swap !== true || id === best || !!next.blocked[id]);

      if (!mine.length) {
        if (!earned) { delete next.added[id]; return; }
        if (next.blocked[id] || next.declined[id]) return;
        // we put it there and it is gone while still earned: the shopper removed it
        if (next.added[id]) { delete next.added[id]; next.declined[id] = true; return; }
        actions.push({ type: 'add', tier: id, variant_id: Number(t.variant_id) });
        return;
      }

      mine.slice(1).forEach(function (l) {
        actions.push({ type: 'remove', tier: id, key: l.key, reason: 'duplicate' });
      });
      var line = mine[0];
      if (!earned) {
        delete next.added[id];
        actions.push({ type: 'remove', tier: id, key: line.key, reason: 'below' });
      } else if (next.blocked[id]) {
        actions.push({ type: 'remove', tier: id, key: line.key, reason: 'blocked' });
      } else if (Number(line.quantity) > 1) {
        // one per order; the price is judged on the next read, at quantity 1
        actions.push({ type: 'set_qty', tier: id, key: line.key, quantity: 1 });
      } else if (Number(line.final_line_price) !== 0 || String(line.variant_id) !== String(t.variant_id)) {
        delete next.added[id];
        next.blocked[id] = true;
        actions.push({ type: 'remove', tier: id, key: line.key, reason: 'not_free' });
      } else {
        next.added[id] = true;
      }
    });
    return { actions: actions, state: next };
  }

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  var helpers = {
    isGiftLine: isGiftLine, activeTiers: activeTiers, giftSubtotal: giftSubtotal,
    emptyState: emptyState, decide: decide, isCartMutation: isCartMutation
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = helpers; return; }

  /* ---------- browser only from here ---------- */

  var CFG = root.WowGiftConfig;
  var CART = root.WowCartConfig;
  // Off switch first: not our group, or the discounts do not exist yet.
  if (!CFG || !CART || CART.arm !== 'B' || CFG.discountLive !== true) return;
  var TIERS = activeTiers(CFG.tiers);
  if (!TIERS.length) return;

  var doc = root.document;
  var STORE_KEY = 'wow_gift_state';
  var MAX_WRITES = 12;       // per page: a hard stop should anything ever loop
  var writes = 0;
  var working = false;
  var ownCalls = 0;
  var drawer = !!(root.WowCart && root.WowCart.ready && root.WowCart.api);

  // Without the drawer we only run on the full cart page. Anywhere else it
  // means our cart failed to start and the shopper has Rebuy's - hands off.
  if (!drawer && CFG.template !== 'cart') return;

  // The visit's memory lives in sessionStorage. If the browser blocks it we
  // could not remember "never offer this again", so we do not run at all.
  function loadState() {
    var raw = root.sessionStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : emptyState(null);
  }
  function saveState(s) { root.sessionStorage.setItem(STORE_KEY, JSON.stringify(s)); }
  try { saveState(loadState()); } catch (e) { return; }

  // XMLHttpRequest on purpose, not fetch (see storefront/wow-cart/README.md:
  // the shipping-protection app only re-prices correctly after an XHR).
  function ownApi(path, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new root.XMLHttpRequest();
      ownCalls++;
      try { xhr.open(body ? 'POST' : 'GET', path); } finally { ownCalls--; }
      xhr.setRequestHeader('Accept', 'application/json');
      if (body) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        var json = null;
        try { json = JSON.parse(xhr.responseText); } catch (e) { /* handled below */ }
        if (xhr.status >= 200 && xhr.status < 300 && json) { resolve(json); return; }
        reject(new Error((json && (json.description || json.message)) || 'Cart error'));
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(body ? JSON.stringify(body) : null);
    });
  }

  // In the drawer we talk through wow-cart's own channel, so it does not take
  // our writes for a change made by the theme and answer them.
  function api(path, body) {
    return drawer ? root.WowCart.api(path, body) : ownApi(path, body);
  }

  function perform(action) {
    writes++;
    if (action.type === 'add') {
      return api('/cart/add.js', { items: [{
        id: action.variant_id, quantity: 1,
        properties: { _wow_src: 'gift', _wow_gift: action.tier }
      }] });
    }
    if (action.type === 'set_qty') return api('/cart/change.js', { id: action.key, quantity: 1 });
    return api('/cart/change.js', { id: action.key, quantity: 0 });
  }

  // Showing the result: the drawer repaints itself (which calls us again, and
  // that second look is the price check). The cart page has to be reloaded.
  function show() {
    if (drawer) return root.WowCart.refresh();
    root.location.reload();
    return Promise.resolve();
  }

  // What the drawer's bar may promise: a gift that came out costing money is
  // no longer offered, so its step leaves the bar. With none left the bar goes
  // back to the single free-shipping step.
  function publish(state) {
    var offered = TIERS.filter(function (t) { return !state.blocked[String(t.threshold_cents)]; });
    root.WowGift = { live: offered.length > 0, tiers: offered, decide: decide };
  }

  function evaluate(cart) {
    if (working) return;
    var state, result;
    try {
      state = loadState();
      result = decide(cart, CFG, state);
      saveState(result.state);
      publish(result.state);
    } catch (e) { return; }
    var actions = result.actions;
    if (!actions.length) return;
    // Out of budget: taking a gift OUT is still allowed, adding is not.
    if (writes >= MAX_WRITES) actions = actions.filter(function (a) { return a.type === 'remove'; });
    if (!actions.length) return;

    working = true;
    var failedAdd = null;
    actions.reduce(function (chain, action) {
      return chain.then(function () {
        return perform(action).catch(function () {
          // sold out or refused: stop offering it, the shopper loses nothing
          if (action.type === 'add') failedAdd = action.tier;
        });
      });
    }, Promise.resolve()).then(function () {
      if (failedAdd) {
        try {
          var s = loadState();
          s.blocked[failedAdd] = true;
          saveState(s);
          publish(s);
        } catch (e) { /* ignore */ }
      }
      working = false;
      return show();
    }).catch(function () { working = false; });
  }

  function readAndEvaluate() {
    api('/cart.js').then(evaluate).catch(function () {});
  }

  /* ---------- Rebuy's gift widget: off, now that ours has started ---------- */

  function retireRebuyGift() {
    function kill() {
      try {
        var list = (root.Rebuy && root.Rebuy.widgets) || [];
        list.forEach(function (w) {
          if (String(w.id) === String(CFG.rebuyWidgetId) && typeof w.destroy === 'function' && !w._wowRetired) {
            w._wowRetired = true;
            w.destroy();
          }
        });
      } catch (e) { /* the CSS in wow-gift.liquid keeps it out of sight regardless */ }
    }
    kill();
    doc.addEventListener('rebuy:widget.ready', kill);
    doc.addEventListener('rebuy.ready', kill);
    doc.addEventListener('rebuy:cart.ready', kill);
    var tries = 0;
    var timer = root.setInterval(function () {
      kill();
      if (++tries >= 20) root.clearInterval(timer);
    }, 500);
  }

  /* ---------- the full cart page: no drawer, so watch the cart ourselves ---------- */

  function watchCart() {
    var timer = null;
    function external(url) {
      // Mule marks its own requests with provider=mule; see wow-cart.js
      return ownCalls === 0 && isCartMutation(url) && !/[?&]provider=mule\b/.test(String(url));
    }
    function changed() {
      root.clearTimeout(timer);
      timer = root.setTimeout(readAndEvaluate, 400);
    }
    var nativeFetch = root.fetch;
    root.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var p = nativeFetch.apply(this, arguments);
      if (external(url)) p.then(function (r) { if (r && r.ok) changed(); }).catch(function () {});
      return p;
    };
    var open = root.XMLHttpRequest.prototype.open;
    root.XMLHttpRequest.prototype.open = function (method, url) {
      if (external(url)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) changed();
        });
      }
      return open.apply(this, arguments);
    };
  }

  function start() {
    try {
      if (drawer) {
        // wow-cart announces every repaint; its first one is still on its way
        doc.addEventListener('wow-cart:render', function (ev) {
          if (ev.detail && ev.detail.cart) evaluate(ev.detail.cart);
        });
      } else {
        watchCart();
        readAndEvaluate();
      }
      // `live` is what makes the drawer draw the three-step bar
      publish(loadState());
      doc.documentElement.classList.add('wow-gift-ready');
      retireRebuyGift();
    } catch (e) {
      // Anything going wrong here must leave the shopper with Rebuy's gift.
      root.WowGift = { live: false, tiers: [] };
      doc.documentElement.classList.remove('wow-gift-ready');
      if (root.console) root.console.error('[wow-gift] start failed', e);
    }
  }

  start();
})(typeof window !== 'undefined' ? window : this);
